/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'

import {
  EuiBadge,
  EuiFlexGroup,
  EuiFlexItem,
  EuiText,
  EuiTextColor,
  useEuiTheme,
} from '@elastic/eui'

import type { AnyProject } from '@modules/project-api/types'
import { FormattedMessage } from '@modules/i18n'
import { isProjectSuspended } from '@modules/project-lib/suspended'

import type { FC } from 'react'

const ProjectNameCell: FC<{ project: AnyProject }> = ({ project }) => {
  const { euiTheme } = useEuiTheme()

  const textStyleSemiBold = { fontWeight: euiTheme.font.weight.semiBold }

  // deleted_at is only present in AnyAdminProject type
  const isDeleted = Boolean('deleted_at' in project.metadata && project.metadata.deleted_at)
  const isSuspended = isProjectSuspended(project)

  const nameElement = isDeleted ? (
    <EuiTextColor color='subdued'>{project.name}</EuiTextColor>
  ) : (
    <EuiText css={textStyleSemiBold}>{project.name}</EuiText>
  )

  return (
    <EuiFlexGroup direction='column' gutterSize='s'>
      <EuiFlexItem>{nameElement}</EuiFlexItem>
      {isDeleted && (
        <EuiFlexItem grow={false}>
          <EuiFlexGroup direction='row' gutterSize='s' alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiBadge color='default'>
                <FormattedMessage
                  id='linkedProjects.project-name-cell.deleted-badge'
                  defaultMessage='Deleted'
                />
              </EuiBadge>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      )}
      {isSuspended && (
        <EuiFlexItem grow={false}>
          <EuiFlexGroup direction='row' gutterSize='s' alignItems='center'>
            <EuiFlexItem grow={false}>
              <EuiBadge color='default'>
                <FormattedMessage
                  id='linkedProjects.project-name-cell.suspended-badge'
                  defaultMessage='Suspended'
                />
              </EuiBadge>
            </EuiFlexItem>
          </EuiFlexGroup>
        </EuiFlexItem>
      )}
    </EuiFlexGroup>
  )
}

export default ProjectNameCell
